import { Children, cloneElement, FunctionComponentElement, HTMLAttributes, ReactNode } from 'react';
import ReactTooltip, { Place } from 'react-tooltip';
import styles from './Tooltip.scss';
import { joinClassNames } from '../../utils';
import variables from './js-variables.scss';

type FCE = FunctionComponentElement<unknown>;

const isComponent = (child: ReactNode): child is FCE => (child as FCE).type.displayName !== undefined;

type TooltipProps = HTMLAttributes<HTMLDivElement> & {
  tooltipId: string;
  text: string | ReactNode;
  /**
   * Accepts only React elements
   */
  children?: ReactNode;
  place?: Place;
};

export const Tooltip = ({
  children,
  tooltipId,
  text,
  place = 'bottom',
  className,
  ...props
}: TooltipProps) => {
  const StyledReactTooltip = () => (
    <ReactTooltip
      id={tooltipId}
      className={styles.tooltipTheme}
      effect="solid"
      place={place}
      backgroundColor={variables.primaryPracticalBlue}
      textColor="#ffffff"
      delayHide={200}
      getContent={() => text}
    />
  );

  if (children) {
    // Can't have multiple children as a trigger
    const singleChild = Children.only(children);

    const tooltipTrigger =
      isComponent(singleChild) && !(singleChild.type.displayName === 'Icon') ? (
        singleChild
      ) : (
        <div>{singleChild}</div>
      );

    const tooltipProps = {
      className: styles.tooltip,
      'aria-describedby': tooltipId,
      'data-for': tooltipId,
      'data-tip': true,
    };

    return (
      <div className={joinClassNames(styles.wrapper, className)} {...props}>
        {cloneElement(tooltipTrigger, tooltipProps)}
        <StyledReactTooltip />
      </div>
    );
  } else {
    return (
      <div className={joinClassNames(styles.wrapper, className)} {...props}>
        <StyledReactTooltip />
      </div>
    );
  }
};

Tooltip.displayName = 'Tooltip';
